<script setup lang="ts">
import { ref } from 'vue';
import { useRouter, RouterLink } from 'vue-router';
import { signIn, authClient } from '../auth';
import AppHeader from '@/components/layout/AppHeader.vue';
import { Button } from '@/components/ui/button';

const router = useRouter();
const email = ref('');
const password = ref('');
const error = ref('');
const loading = ref(false);

async function handleSubmit() {
  error.value = '';
  loading.value = true;

  try {
    const result = await signIn.email({
      email: email.value,
      password: password.value,
    });

    if (result.error) {
      error.value = result.error.message || 'Sign in failed';
      return;
    }

    // Verify session was established
    const session = await authClient.getSession();
    if (!session.data?.user) {
      error.value = 'Sign in appeared to succeed but session was not established';
      return;
    }

    router.push('/decks');
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'An error occurred';
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <div class="min-h-screen bg-background">
    <AppHeader />

    <main class="max-w-md mx-auto px-4 py-12">
      <div class="bg-card rounded-lg shadow-md p-8 border border-border">
        <h1 class="text-2xl font-bold text-center mb-6 text-foreground">
          Sign In
        </h1>

        <form
          class="space-y-4"
          @submit.prevent="handleSubmit"
        >
          <div>
            <label
              for="email"
              class="block text-sm font-medium text-foreground mb-1"
            >
              Email
            </label>
            <input
              id="email"
              v-model="email"
              type="email"
              required
              class="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
          </div>

          <div>
            <label
              for="password"
              class="block text-sm font-medium text-foreground mb-1"
            >
              Password
            </label>
            <input
              id="password"
              v-model="password"
              type="password"
              required
              class="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
          </div>

          <div
            v-if="error"
            class="text-destructive text-sm"
          >
            {{ error }}
          </div>

          <Button
            type="submit"
            :disabled="loading"
            class="w-full"
          >
            {{ loading ? 'Signing in...' : 'Sign In' }}
          </Button>
        </form>

        <p class="mt-4 text-center text-sm text-muted-foreground">
          Don't have an account?
          <RouterLink
            to="/sign-up"
            class="text-primary hover:underline"
          >
            Sign up
          </RouterLink>
        </p>
      </div>
    </main>
  </div>
</template>
